import React, { useState, useEffect } from 'react';
import { Search, Eye, Filter, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import styles from '../Admins/Admins.module.css';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/superadmin/audit-logs');
      setLogs(data);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredLogs = logs.filter(log => {
    const search = searchTerm.toLowerCase();
    const action = log.action?.toLowerCase() || '';
    const module = log.module?.toLowerCase() || '';
    const actor = log.actor?.toLowerCase() || '';
    const desc = log.description?.toLowerCase() || '';
    
    return action.includes(search) || module.includes(search) || actor.includes(search) || desc.includes(search);
  });

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Success':
        return { background: '#ecfdf5', color: '#059669' };
      case 'Failed':
        return { background: '#fef2f2', color: '#ef4444' };
      case 'Info':
      default:
        return { background: '#eff6ff', color: '#2563eb' };
    }
  };

  return (
    <div className={styles.adminsContainer}>
      <div className={styles.pageHeader}>
        <div className={styles.headerText}>
          <h2>System Audit Logs</h2>
          <p>Global timeline of recent activities across all modules</p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.editBtn} style={{ background: 'white', border: '1px solid #e2e8f0', padding: '8px 16px', borderRadius: '8px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500 }} onClick={() => toast.info('Export started')}>
            <Download size={16} /> Export Logs
          </button>
        </div>
      </div>

      <div className={styles.searchBar} style={{ marginBottom: '24px', maxWidth: '400px' }}>
        <Search size={18} className={styles.searchIcon} />
        <input 
          type="text" 
          placeholder="Search by action, module, or user..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {loading ? (
        <div className={styles.loading}>Loading system audit logs...</div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Event & Description</th>
                <th>Module</th>
                <th>Actor (User)</th>
                <th>Status</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length > 0 ? (
                filteredLogs.map(log => (
                  <tr key={log._id}>
                    <td>
                      <div>
                        <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '4px', fontSize: '13px' }}>
                          {log.action}
                        </div>
                        <div style={{ fontSize: '12px', color: '#475569' }}>
                          {log.description}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span style={{
                        padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                        backgroundColor: '#f1f5f9',
                        color: '#475569',
                        textTransform: 'uppercase'
                      }}>
                        {log.module}
                      </span>
                    </td>
                    <td>
                      <div className={styles.adminInfo}>
                        <div className={styles.avatar} style={{ width: '28px', height: '28px', fontSize: '12px' }}>
                          {(log.actor?.charAt(0) || '?').toUpperCase()}
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: 500 }}>{log.actor}</span>
                      </div>
                    </td>
                    <td>
                      <span className={styles.statusBadge} style={getStatusStyle(log.status)}>
                        {log.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: '13px', color: '#1e293b' }}>
                        {new Date(log.timestamp).toLocaleDateString('en-GB')}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className={styles.noData}>No audit logs found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
